'use client';

import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import useSWR from 'swr';
import { useLogger } from '@lib/utils/logUtils';

// Type for our cache entries
type ImageCacheEntry = {
  url: string;
  status: 'success' | 'error';
  timestamp: number;
};

// Cache context
type ProfileImageCacheContextType = {
  getCachedImage: (src: string | null | undefined) => ImageCacheEntry | null;
  setCachedImage: (src: string | null | undefined, status: 'success' | 'error') => void;
};

const ProfileImageCacheContext = createContext<ProfileImageCacheContextType | null>(null);

// Local storage key
const LOCAL_STORAGE_KEY = 'profile-image-cache';
// Cache expiration time (24 hours)
const CACHE_EXPIRATION = 24 * 60 * 60 * 1000;

// Create a simple in-memory request cache to prevent duplicate requests
const pendingRequests = new Map<string, Promise<string>>();
const successfulRequests = new Set<string>();
const failedRequests = new Set<string>();

// Helper function to throttle image requests
const requestQueue: Array<() => Promise<any>> = [];
let isProcessingQueue = false;

async function processRequestQueue() {
  if (isProcessingQueue || requestQueue.length === 0) return;
  
  isProcessingQueue = true;
  
  try {
    // Process one request at a time with a small delay to avoid rate limiting
    const nextRequest = requestQueue.shift();
    if (nextRequest) {
      await nextRequest();
      // Add a small delay before processing the next request
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  } finally {
    isProcessingQueue = false;
    if (requestQueue.length > 0) {
      processRequestQueue();
    }
  }
}

// Enqueue a request and return a promise that resolves when the request is processed
function enqueueRequest<T>(requestFn: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    requestQueue.push(async () => {
      try {
        const result = await requestFn();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    });
    
    processRequestQueue();
  });
}

// Helper function to determine if an image is from Google
export function isGoogleImage(src: string | null | undefined): boolean {
  if (!src) return false;
  
  // Check for various Google image domains
  return src.includes('googleusercontent.com') || 
         src.includes('google') && src.includes('usercontentauth') ||
         src.includes('lh3.google.com') ||
         src.includes('lh4.google.com') ||
         src.includes('lh5.google.com') ||
         src.includes('lh6.google.com');
}

// Helper function to clean Google image URLs
export function cleanGoogleImageUrl(src: string, size: number): string {
  if (!src) return '';
  
  // First, strip any cache-busting parameters (-cb= or cb=)
  let baseUrl = src;
  
  // Handle the case where it's part of the size parameter with -cb=
  if (baseUrl.includes('-cb=')) {
    baseUrl = baseUrl.split('-cb=')[0];
  }
  
  // Handle the case where it's a separate parameter with cb=
  if (baseUrl.includes('cb=')) {
    baseUrl = baseUrl.split('cb=')[0];
    // Remove trailing & or ? if present
    if (baseUrl.endsWith('&') || baseUrl.endsWith('?')) {
      baseUrl = baseUrl.slice(0, -1);
    }
  }
  
  // Now strip all parameters and get only the base part
  // If URL has a size parameter or any other params
  if (baseUrl.includes('=')) {
    baseUrl = baseUrl.split('=')[0];
  }
  
  // Return the base URL with just a simple size parameter
  // Just =s{size} with no crop (-c) or any other params
  return `${baseUrl}=s${size}`;
}

export function ProfileImageCacheProvider({ children }: { children: ReactNode }) {
  const logger = useLogger('ProfileImageCache');
  const [cache, setCache] = useState<Record<string, ImageCacheEntry>>({});
  
  // Load initial cache from localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    try {
      const storedCache = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (storedCache) {
        const parsedCache = JSON.parse(storedCache) as Record<string, ImageCacheEntry>;
        
        // Clean up expired entries
        const now = Date.now();
        const cleanedCache = Object.entries(parsedCache).reduce((acc, [key, entry]) => {
          if (now - entry.timestamp < CACHE_EXPIRATION) {
            acc[key] = entry;
          }
          return acc;
        }, {} as Record<string, ImageCacheEntry>);
        
        setCache(cleanedCache);
      }
    } catch (error) {
      logger.error('Error loading image cache from localStorage:', error);
    }
  }, []);
  
  // Update localStorage when cache changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    if (Object.keys(cache).length > 0) {
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cache));
      } catch (error) {
        logger.error('Error saving image cache to localStorage:', error);
      }
    }
  }, [cache]);
  
  // Get a cached image result
  const getCachedImage = (src: string | null | undefined): ImageCacheEntry | null => {
    if (!src) return null;
    return cache[src] || null;
  };
  
  // Set a cached image result
  const setCachedImage = (src: string | null | undefined, status: 'success' | 'error') => {
    if (!src) return;
    
    setCache(prevCache => ({
      ...prevCache,
      [src]: {
        url: src,
        status,
        timestamp: Date.now()
      }
    }));
  };
  
  return (
    <ProfileImageCacheContext.Provider value={{ getCachedImage, setCachedImage }}>
      {children}
    </ProfileImageCacheContext.Provider>
  );
}

// Hook to use the profile image cache
export function useProfileImageCache() {
  const context = useContext(ProfileImageCacheContext);
  if (!context) {
    throw new Error('useProfileImageCache must be used within a ProfileImageCacheProvider');
  }
  return context;
}

// Custom fetcher for profile images using SWR
export function useProfileImage(src: string | null | undefined, size: number) {
  const logger = useLogger('useProfileImage');
  const { getCachedImage, setCachedImage } = useProfileImageCache();
  
  // Detect if image is from Google right away
  const isSourceGoogleImage = isGoogleImage(src);
  const normalizedSrc = src || '';
  
  // Initialize state that will be used regardless of image source type
  const [imageState, setImageState] = useState<{
    imageSrc: string | null;
    isLoading: boolean;
    hasError: boolean;
  }>({
    imageSrc: normalizedSrc || null,
    isLoading: !!normalizedSrc,
    hasError: !normalizedSrc
  });

  // Use SWR for non-Google images (but always declare it to maintain hooks order)
  // Custom fetcher with retries for non-Google images
  const imageFetcher = async (url: string) => {
    // Don't load empty URLs
    if (!url) throw new Error('Empty URL');
    
    // First check in-memory cache
    const cachedEntry = getCachedImage(url);
    if (cachedEntry) {
      if (cachedEntry.status === 'error') {
        throw new Error('Image previously failed to load');
      }
      return url;
    }
    
    // Check if we already have a pending request for this URL
    if (pendingRequests.has(url)) {
      return pendingRequests.get(url);
    }
    
    // Check in-memory success/failure caches
    if (successfulRequests.has(url)) return url;
    if (failedRequests.has(url)) throw new Error('Image previously failed to load');
    
    // Helper function to load an image and return a promise
    const loadImage = (imageUrl: string): Promise<string> => {
      return new Promise((resolve, reject) => {
        const img = new window.Image();
        img.onload = () => {
          setCachedImage(imageUrl, 'success');
          successfulRequests.add(imageUrl);
          resolve(imageUrl);
        };
        img.onerror = () => {
          failedRequests.add(imageUrl);
          reject(new Error(`Failed to load image: ${imageUrl}`));
        };
        img.src = imageUrl;
      });
    };
    
    // Create the request promise and store it
    const requestPromise = enqueueRequest(() => loadImage(url))
      .catch(error => {
        logger.error('Error loading image:', error);
        setCachedImage(url, 'error');
        throw error;
      })
      .finally(() => {
        // Remove from pending requests when done
        pendingRequests.delete(url);
      });
    
    pendingRequests.set(url, requestPromise);
    return requestPromise;
  };
  
  // Use SWR regardless of image source, but only perform fetching for non-Google images
  const { data, error, isLoading: swrIsLoading } = useSWR(
    normalizedSrc && !isSourceGoogleImage ? normalizedSrc : null, 
    imageFetcher,
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      dedupingInterval: 24 * 60 * 60 * 1000 // 24 hours
    }
  );
  
  // Handle Google images with useEffect
  useEffect(() => {
    let isMounted = true;
    
    // Skip if not a Google image or empty source
    if (!isSourceGoogleImage || !normalizedSrc) {
      if (!normalizedSrc) {
        setImageState({
          imageSrc: null,
          isLoading: false,
          hasError: true
        });
      }
      return;
    }
    
    // First check the cache
    const cachedEntry = getCachedImage(normalizedSrc);
    if (cachedEntry) {
      setImageState({
        imageSrc: cachedEntry.status === 'success' ? cachedEntry.url : null,
        isLoading: false,
        hasError: cachedEntry.status === 'error'
      });
      return;
    }
    
    // Check in-memory caches
    if (successfulRequests.has(normalizedSrc)) {
      setImageState({
        imageSrc: normalizedSrc,
        isLoading: false,
        hasError: false
      });
      return;
    }
    
    if (failedRequests.has(normalizedSrc)) {
      setImageState({
        imageSrc: null,
        isLoading: false,
        hasError: true
      });
      return;
    }
    
    // Check if we already have a pending request
    if (pendingRequests.has(normalizedSrc)) {
      const pendingRequest = pendingRequests.get(normalizedSrc);
      if (pendingRequest) {
        pendingRequest
          .then(url => {
            if (isMounted) {
              setImageState({
                imageSrc: url,
                isLoading: false,
                hasError: false
              });
            }
          })
          .catch(() => {
            if (isMounted) {
              setImageState({
                imageSrc: null,
                isLoading: false,
                hasError: true
              });
            }
          });
      }
      return;
    }
    
    // Helper function to load an image and return a promise
    const loadImage = (imageUrl: string): Promise<string> => {
      return new Promise((resolve, reject) => {
        const img = new window.Image();
        img.onload = () => {
          setCachedImage(imageUrl, 'success');
          successfulRequests.add(imageUrl);
          resolve(imageUrl);
        };
        img.onerror = () => {
          failedRequests.add(imageUrl);
          reject(new Error(`Failed to load image: ${imageUrl}`));
        };
        img.src = imageUrl;
      });
    };
    
    // Create a clean URL with appropriate size
    const cleanedUrl = cleanGoogleImageUrl(normalizedSrc, size);
    
    // Try loading the image with the cleaned URL through the queue
    const requestPromise = enqueueRequest(() => loadImage(cleanedUrl))
      .then(url => {
        if (isMounted) {
          setImageState({
            imageSrc: url,
            isLoading: false,
            hasError: false
          });
        }
        return url;
      })
      .catch(error => {
        // Log error with better formatting
        logger.error(`Google image loading failed: ${error.message || 'Unknown error'}`);
        
        setCachedImage(normalizedSrc, 'error');
        if (isMounted) {
          setImageState({
            imageSrc: null,
            isLoading: false,
            hasError: true
          });
        }
        throw error;
      })
      .finally(() => {
        pendingRequests.delete(normalizedSrc);
      });
    
    pendingRequests.set(normalizedSrc, requestPromise);
    
    return () => { 
      isMounted = false;
    };
  }, [normalizedSrc, size, isSourceGoogleImage]);

  // Update state for non-Google images when SWR data changes
  useEffect(() => {
    if (isSourceGoogleImage) {
      return;
    }
    
    if (error) {
      setImageState({
        imageSrc: null,
        isLoading: false,
        hasError: true
      });
    } else if (data) {
      setImageState({
        imageSrc: data,
        isLoading: false,
        hasError: false
      });
    } else if (swrIsLoading) {
      setImageState({
        imageSrc: null,
        isLoading: true,
        hasError: false
      });
    }
  }, [data, error, swrIsLoading, isSourceGoogleImage]);
  
  return {
    imageSrc: imageState.imageSrc,
    isGoogleImage: isSourceGoogleImage,
    isLoading: imageState.isLoading,
    hasError: imageState.hasError
  };
} 